/**
 * multiway.ts —— 多路（>2 人）已知底牌的赢率精算（第 18 轮，遗留项 L1）。
 *
 * 口径说明（只用于**复盘**，绝不用于对局中）：
 *   · 输入 = 你的 2 张底牌 + 每位已摊牌对手的 2 张底牌 + 当街已开公共牌；
 *   · 对剩余未开公共牌做**全量枚举**（零采样、零近似，与单机口径一致）：
 *     最坏情形是 3 人翻牌前 C(46,5)=1,370,754 个牌面，实测数百毫秒，放在 Worker 里完全可接受；
 *   · 平分底池按并列人数折算（equity = win + tie/并列人数），与真实分池口径一致；
 *   · `exact` 字段显式标注本次是否为无采样精算，UI 必须据此标注口径。
 *
 * 零 DOM 依赖：可直接在 Web Worker 中 import（见 src/workers/multiway.worker.ts）。
 */

import { NUM_CARDS, assertDistinctCards, type Card } from './cards';
import { evaluateMasks } from './evaluator';

/**
 * 全量枚举的牌面上限（安全阀）。
 * 6 人桌最坏情形为 3 人翻牌前 C(46,5)=1,370,754，远低于此值；超过则直接报错而**不做近似**，
 * 由 UI 回落到既有「你 vs 1 名随机对手」口径，绝不引入近似数值。
 */
export const EXACT_MAX_BOARDS = 2_000_000;

export interface MultiwayResult {
  /** 英雄的底池份额期望（含平分折算），[0,1]。 */
  readonly equity: number;
  /** 独赢概率。 */
  readonly win: number;
  /** 与人并列（需分池）的概率。 */
  readonly tie: number;
  /** 被击败的概率。 */
  readonly lose: number;
  /** 恒为 true —— 本引擎无近似路径。 */
  readonly exact: true;
  /** 实际参与统计的完整牌面数。 */
  readonly boards: number;
  /** 对手数（已知底牌）。 */
  readonly villains: number;
}

function combinations(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  let r = 1;
  for (let i = 0; i < k; i++) r = (r * (n - i)) / (i + 1);
  return Math.round(r);
}

function maskOf(cards: readonly Card[]): number[] {
  const m = [0, 0, 0, 0];
  for (const c of cards) m[c & 3] |= 1 << (c >>> 2);
  return m;
}

/**
 * 多路已知底牌赢率。
 * @param heroHole   英雄 2 张底牌
 * @param villainHoles 每位对手的 2 张底牌（长度 ≥ 1）
 * @param board      已开公共牌（0/3/4/5 张）
 */
export function multiwayEquity(
  heroHole: readonly Card[],
  villainHoles: readonly (readonly Card[])[],
  board: readonly Card[] = [],
): MultiwayResult {
  if (heroHole.length !== 2) throw new Error(`底牌必须是 2 张，收到 ${heroHole.length} 张`);
  if (villainHoles.length < 1) throw new Error('至少需要 1 名已知底牌的对手');
  for (const v of villainHoles) if (v.length !== 2) throw new Error('对手底牌必须是 2 张');
  const all = [...heroHole, ...villainHoles.flat(), ...board];
  assertDistinctCards(all, 'multiwayEquity 输入');

  const used = new Uint8Array(NUM_CARDS);
  for (const c of all) used[c] = 1;
  const pool: number[] = [];
  for (let c = 0; c < NUM_CARDS; c++) if (used[c] === 0) pool.push(c);

  const need = 5 - board.length;
  const base = maskOf(board);
  const hMask = maskOf(heroHole);
  const vMasks = villainHoles.map((v) => maskOf(v));

  let win = 0;
  let tie = 0;
  let lose = 0;
  let share = 0;
  let boards = 0;

  /** 统计一个完整牌面（f 为该牌面的 4 个花色位图）。 */
  const tally = (f: number[]): void => {
    const hv = evaluateMasks(f[0] | hMask[0], f[1] | hMask[1], f[2] | hMask[2], f[3] | hMask[3]);
    let ties = 1; // 与英雄并列的人数（含英雄自己）
    let beaten = false;
    for (const vm of vMasks) {
      const vv = evaluateMasks(f[0] | vm[0], f[1] | vm[1], f[2] | vm[2], f[3] | vm[3]);
      if (vv > hv) { beaten = true; break; }
      if (vv === hv) ties++;
    }
    boards++;
    if (beaten) { lose++; return; }
    if (ties > 1) { tie++; share += 1 / ties; return; }
    win++; share += 1;
  };

  const total = need === 0 ? 1 : combinations(pool.length, need);
  if (total > EXACT_MAX_BOARDS) {
    throw new Error(`多路精算牌面数 ${total} 超过上限 ${EXACT_MAX_BOARDS}`);
  }

  if (need === 0) {
    tally([base[0], base[1], base[2], base[3]]);
  } else {
    const pick = new Int32Array(need);
    const walk = (start: number, depth: number): void => {
      if (depth === need) {
        const f = [base[0], base[1], base[2], base[3]];
        for (let i = 0; i < need; i++) {
          const c = pool[pick[i]];
          f[c & 3] |= 1 << (c >>> 2);
        }
        tally(f);
        return;
      }
      for (let i = start; i < pool.length; i++) { pick[depth] = i; walk(i + 1, depth + 1); }
    };
    walk(0, 0);
  }

  return {
    equity: share / boards,
    win: win / boards,
    tie: tie / boards,
    lose: lose / boards,
    exact: true,
    boards,
    villains: villainHoles.length,
  };
}
